import {
  AxiosRequestConfig,
  AxiosResponse,
  Method,
  RawAxiosRequestHeaders,
} from 'axios';
import axiosClient, { publicAxiosClient } from './axiosClient';

export interface ApiRequestConfig<TData = unknown> {
  url: string;
  method?: Method;
  params?: AxiosRequestConfig['params'];
  data?: TData;
  headers?: RawAxiosRequestHeaders;
  timeout?: number;
  responseType?: AxiosRequestConfig['responseType'];
  validateStatus?: AxiosRequestConfig['validateStatus'];
}

export async function apiRequest<TResponse = unknown, TData = unknown>(
  config: ApiRequestConfig<TData>
): Promise<AxiosResponse<TResponse>> {
  return axiosClient.request<TResponse>({
    url: config.url,
    method: config.method ?? 'GET',
    params: config.params,
    data: config.data,
    headers: config.headers,
    timeout: config.timeout,
    responseType: config.responseType,
    validateStatus: config.validateStatus,
  });
}

export async function publicRequest<TResponse = unknown, TData = unknown>(
  config: ApiRequestConfig<TData>
): Promise<AxiosResponse<TResponse>> {
  return publicAxiosClient.request<TResponse>({
    url: config.url,
    method: config.method ?? 'GET',
    params: config.params,
    data: config.data,
    headers: config.headers,
    timeout: config.timeout,
    responseType: config.responseType,
    validateStatus: config.validateStatus,
  });
}

export const apiGet = <TResponse = unknown>(
  url: string,
  config?: Omit<ApiRequestConfig<never>, 'url' | 'method' | 'data'>
) => apiRequest<TResponse>({ ...config, url, method: 'GET' });

export const apiPost = <TResponse = unknown, TData = unknown>(
  url: string,
  data?: TData,
  config?: Omit<ApiRequestConfig<TData>, 'url' | 'method' | 'data'>
) => apiRequest<TResponse, TData>({ ...config, url, method: 'POST', data });

export const apiPut = <TResponse = unknown, TData = unknown>(
  url: string,
  data?: TData,
  config?: Omit<ApiRequestConfig<TData>, 'url' | 'method' | 'data'>
) => apiRequest<TResponse, TData>({ ...config, url, method: 'PUT', data });

export const apiDelete = <TResponse = unknown>(
  url: string,
  config?: Omit<ApiRequestConfig<never>, 'url' | 'method' | 'data'>
) => apiRequest<TResponse>({ ...config, url, method: 'DELETE' });
